import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import FamilyMembers from './pages/FamilyMembers';
import Portfolio from './pages/Portfolio';
import LASManagement from './pages/LASManagement';
import MTFManagement from './pages/MTFManagement';
import DematAccounts from './pages/DematAccounts';
import Transactions from './pages/Transactions';
import CapitalGains from './pages/CapitalGains';
import BulkImportTransactions from './pages/BulkImportTransactions';
import BulkImportDividends from './pages/BulkImportDividends';
import ImportHoldingsSnapshot from './pages/ImportHoldingsSnapshot';
// LoanPayments, PledgeFlow, BajajUpdate are embedded as tabs inside DebtManagement.
import Imports from './pages/Imports';
import MemberAccess from './pages/MemberAccess';
import Banks from './pages/Banks';
import Performance from './pages/Performance';
import DebtManagement from './pages/DebtManagement';
import Companies from './pages/Companies';
import Accounts from './pages/Accounts';
import LandingPage from './pages/LandingPage';
import Calculator from './pages/Calculator';
import AboutUs from './pages/AboutUs';
import FamilySetup from './pages/FamilySetup';
import FamilyPicker from './pages/FamilyPicker';
import FamilySettings from './pages/FamilySettings';
import AdminDashboard from './pages/AdminDashboard';
import ChangePassword from './pages/ChangePassword';
import RentalManagement from './pages/RentalManagement';
import Properties from './pages/Properties';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import { FamilyProvider } from './contexts/FamilyContext';
import { Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 30000, // 30 second timeout
});

// Helper to format API error details (handles string, array, object)
export function formatApiError(err) {
  // No response at all — network/CORS/timeout
  if (!err.response) {
    if (err.code === 'ECONNABORTED') return 'Request timed out. Please check your connection and try again.';
    if (err.message?.includes('Network Error')) return 'Cannot reach the server. Please check your internet connection.';
    return `Connection error: ${err.message || 'Unknown'}`;
  }
  const detail = err.response?.data?.detail;
  if (detail == null) return `Server error (${err.response.status}). Please try again.`;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join('; ');
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return String(detail);
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor with automatic retry for network errors
// Endpoints listed here will NEVER be auto-retried (long-running / expensive)
const NO_RETRY_URLS = ['/prices/refresh', '/prices/refresh-mtf', '/imports/', '/transactions/bulk'];
const shouldSkipRetry = (cfg) => NO_RETRY_URLS.some(u => (cfg?.url || '').includes(u));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    // Retry once on network error or 503 (service unavailable) — but not if already retried
    // Skip retry for long-running / non-idempotent endpoints
    if (!config._retried && !shouldSkipRetry(config) && (!error.response || error.response.status === 503)) {
      config._retried = true;
      // Wait 1 second before retrying
      await new Promise(r => setTimeout(r, 1000));
      return api(config);
    }
    return Promise.reject(error);
  }
);

// Protected Route wrapper with Family context
const ProtectedRoute = ({ user, setUser, children }) => {
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return (
    <FamilyProvider user={user} setUser={setUser}>
      {children}
    </FamilyProvider>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsFamilySetup, setNeedsFamilySetup] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Try to validate the existing session with retry
      const validateSession = async () => {
        for (let attempt = 1; attempt <= 2; attempt++) {
          try {
            const res = await api.get('/auth/me');
            setUser(res.data.user);
            setNeedsFamilySetup(res.data.needs_family_setup);
            return;
          } catch (err) {
            // Auth error (401/403) — token is invalid, clear it
            if (err.response && err.response.status < 500) {
              localStorage.removeItem('token');
              return;
            }
            // Network/server error — retry once (server might be waking up)
            if (attempt < 2) {
              await new Promise(r => setTimeout(r, 2000));
              continue;
            }
            // Give up after retries — clear token so user can re-login
            localStorage.removeItem('token');
          }
        }
      };
      validateSession().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public routes - always accessible */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/login" element={!user ? <Login setUser={setUser} setNeedsFamilySetup={setNeedsFamilySetup} /> : 
            (needsFamilySetup ? <Navigate to="/family-setup" /> : <Navigate to="/dashboard" />)} />
          
          {/* Family Setup - for new users or joining a family */}
          <Route path="/family-setup" element={
            user ? (
              <FamilyProvider user={user} setUser={setUser}>
                <FamilySetup />
              </FamilyProvider>
            ) : <Navigate to="/login" />
          } />
          
          {/* Family Picker - for users in multiple families */}
          <Route path="/family-picker" element={
            user ? (
              <FamilyProvider user={user} setUser={setUser}>
                <FamilyPicker />
              </FamilyProvider>
            ) : <Navigate to="/login" />
          } />

          {/* Change Password - for forced password change */}
          <Route path="/change-password" element={
            user ? (
              <ChangePassword 
                user={user} 
                isForced={true}
                onComplete={() => window.location.href = '/dashboard'} 
              />
            ) : <Navigate to="/login" />
          } />

          {/* Family Settings */}
          <Route path="/settings/family" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <FamilySettings user={user} setUser={setUser} />
            </ProtectedRoute>
          } />

          {/* Admin Dashboard */}
          <Route path="/admin" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <AdminDashboard user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Dashboard user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/family-members" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <FamilyMembers user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/portfolio/:memberId" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Portfolio user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/las" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <LASManagement user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/mtf" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <MTFManagement user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/demat" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <DematAccounts user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/transactions" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Transactions user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/bulk-import" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <BulkImportTransactions user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/imports/holdings-snapshot" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <ImportHoldingsSnapshot user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/bulk-dividends" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <BulkImportDividends user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/bajaj-update" element={<Navigate to="/debt-management?tab=daily-update" replace />} />
          <Route path="/debt-update" element={<Navigate to="/debt-management?tab=daily-update" replace />} />
          <Route path="/loan-payments" element={<Navigate to="/debt-management?tab=payments" replace />} />
          <Route path="/pledge-flow" element={<Navigate to="/debt-management?tab=pledge-flow" replace />} />
          <Route path="/capital-gains" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <CapitalGains user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/banks" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Banks user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/accounts" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Accounts user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/performance" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Performance user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/debt-management" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <DebtManagement user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/imports" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Imports user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/companies" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Companies user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/rentals" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <RentalManagement user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/properties" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Properties user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/projects" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <Projects user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/projects/:projectId" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <ProjectDetail user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          <Route path="/member-access" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <MemberAccess user={user} setUser={setUser} />
            </ProtectedRoute>
          } />
          
          {/* Redirects for old URLs */}
          <Route path="/bulk-upload" element={<Navigate to="/imports" replace />} />
          <Route path="/symbol-management" element={<Navigate to="/imports" replace />} />
          <Route path="/data-integrity" element={<Navigate to="/imports" replace />} />
          <Route path="/dividends" element={<Navigate to="/performance" replace />} />
          
          {/* Invite acceptance */}
          <Route path="/invite/:code" element={
            user ? (
              <FamilyProvider user={user} setUser={setUser}>
                <FamilySetup />
              </FamilyProvider>
            ) : <Navigate to="/login" />
          } />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;